import React from 'react';
import { useTranslation } from 'react-i18next';
import { BillFrequency, RecurringAmount } from '../../utils/onboardingCategoryBuilder';
import { getTranslatedCountries, getTranslatedBankCountries } from '../../utils/countries';
import { getAllCurrencies } from '../../utils/shared';
import {
  parseAmount,
  parseDay,
  parseSignedAmount,
  parseYears
} from '../../utils/amountInput';

export {
  parseAmount,
  parseDay,
  parseSignedAmount,
  parseYears,
  isSignedAmountInput
} from '../../utils/amountInput';

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
] as const;

export const CountrySelect: React.FC<{
  value: string;
  onChange: (countryId: string) => void;
  label: string;
  required?: boolean;
}> = ({ value, onChange, label, required }) => {
  const { t } = useTranslation();
  const countries = React.useMemo(
    () => getTranslatedCountries(t),
    [t]
  );
  return (
    <label className="builder-field">
      <span>{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} required={required}>
        <option value="">{t('builder.fields.chooseCountry', { ns: 'onboarding' })}</option>
        {countries.map(country => (
          <option key={country.id} value={country.id}>
            {country.flag} {country.name}
          </option>
        ))}
      </select>
    </label>
  );
};

export const BankCountrySelect: React.FC<{
  value: string;
  onChange: (countryId: string) => void;
  label: string;
  required?: boolean;
}> = ({ value, onChange, label, required }) => {
  const { t } = useTranslation();
  const countries = React.useMemo(
    () => getTranslatedBankCountries(t),
    [t]
  );
  return (
    <label className="builder-field">
      <span>{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} required={required}>
        <option value="">{t('builder.fields.chooseCountry', { ns: 'onboarding' })}</option>
        {countries.map(country => (
          <option key={country.id} value={country.id}>
            {country.flag} {country.name}
          </option>
        ))}
      </select>
    </label>
  );
};

export const AmountCurrencySelect: React.FC<{
  value: string;
  onChange: (currency: string) => void;
  id?: string;
}> = ({ value, onChange, id }) => {
  const { t } = useTranslation();
  const currencies = getAllCurrencies();

  return (
    <select
      id={id}
      className="builder-amount-currency-select"
      value={value}
      onChange={e => onChange(e.target.value)}
      aria-label={t('builder.fields.amountCurrency', { ns: 'onboarding' })}
    >
      {currencies.map(currency => (
        <option key={currency} value={currency}>
          {currency}
        </option>
      ))}
    </select>
  );
};

export const RecurringFields: React.FC<{
  bill: RecurringAmount;
  onChange: (bill: RecurringAmount) => void;
  showFrequency?: boolean;
  amountLabel?: string;
  defaultCurrency?: string;
}> = ({ bill, onChange, showFrequency = true, amountLabel, defaultCurrency = 'USD' }) => {
  const { t } = useTranslation();
  const amountText = amountLabel ?? t('builder.fields.amount', { ns: 'onboarding' });
  const currency = bill.currency || defaultCurrency;

  const updateBill = (patch: Partial<RecurringAmount>) => {
    onChange({
      ...bill,
      currency: bill.currency || defaultCurrency,
      ...patch
    });
  };

  return (
    <div className="builder-recurring">
      <label className="builder-field builder-field-amount">
        <span>{amountText}</span>
        <div className="builder-amount-input">
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={bill.amount ?? ''}
            placeholder="0"
            onChange={e =>
              updateBill({
                amount: parseAmount(e.target.value)
              })
            }
          />
          <AmountCurrencySelect
            value={currency}
            onChange={next => updateBill({ currency: next })}
          />
        </div>
      </label>

      {showFrequency && (
        <div className="builder-recurring-meta">
          <label className="builder-field">
            <span>{t('builder.fields.frequency', { ns: 'onboarding' })}</span>
            <select
              value={bill.frequency}
              onChange={e => updateBill({ frequency: e.target.value as BillFrequency })}
            >
              <option value="monthly">{t('builder.fields.monthly', { ns: 'onboarding' })}</option>
              <option value="quarterly">{t('builder.fields.quarterly', { ns: 'onboarding' })}</option>
              <option value="yearly">{t('builder.fields.yearly', { ns: 'onboarding' })}</option>
            </select>
          </label>

          {bill.frequency === 'monthly' ? (
            <label className="builder-field">
              <span>{t('builder.fields.dueDay', { ns: 'onboarding' })}</span>
              <input
                type="number"
                min="1"
                max="28"
                value={bill.dueDay ?? ''}
                placeholder="1–28"
                onChange={e => updateBill({ dueDay: parseDay(e.target.value) })}
              />
            </label>
          ) : bill.frequency === 'yearly' ? (
            <label className="builder-field">
              <span>{t('builder.fields.dueMonth', { ns: 'onboarding' })}</span>
              <select
                value={bill.dueMonth ?? ''}
                onChange={e =>
                  updateBill({
                    dueMonth: e.target.value ? Number(e.target.value) : null
                  })
                }
              >
                <option value="">{t('builder.fields.chooseMonth', { ns: 'onboarding' })}</option>
                {MONTH_KEYS.map((key, index) => (
                  <option key={key} value={index + 1}>
                    {t(`builder.months.${key}`, { ns: 'onboarding' })}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      )}
    </div>
  );
};

export const UtilityToggle: React.FC<{
  label: string;
  enabled: boolean;
  bill: RecurringAmount;
  onChange: (enabled: boolean, bill: RecurringAmount) => void;
  defaultCurrency?: string;
}> = ({ label, enabled, bill, onChange, defaultCurrency }) => (
  <div className={`builder-utility ${enabled ? '' : 'disabled'}`}>
    <label className="builder-check">
      <input
        type="checkbox"
        checked={enabled}
        onChange={e => onChange(e.target.checked, bill)}
      />
      <span>{label}</span>
    </label>
    {enabled && (
      <RecurringFields
        bill={bill}
        onChange={next => onChange(enabled, next)}
        defaultCurrency={defaultCurrency}
      />
    )}
  </div>
);

export const CardRemoveButton: React.FC<{
  onClick: () => void;
  label: string;
}> = ({ onClick, label }) => (
  <button type="button" className="builder-remove" onClick={onClick} aria-label={label}>
    ×
  </button>
);
